import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { listingsApi } from '../lib/api';
import ListingCard from '../components/ListingCard';
import { Search, Shield, Star, Heart, ArrowRight, MapPin, Calendar } from 'lucide-react';

const heroImage = 'https://images.unsplash.com/photo-1732123280078-27d7997ab0fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxob3JzZSUyMGdyYXppbmclMjBwYXN0dXJlJTIwc3Vuc2V0fGVufDB8fHx8MTc2NzMyMTg4NHww&ixlib=rb-4.1.0&q=85';

const features = [
  {
    icon: Shield,
    title: 'Verified Hosts',
    description: 'Every listing is reviewed and approved to ensure quality care for your animals.',
  },
  {
    icon: Star,
    title: 'Trusted Reviews',
    description: 'Real reviews from fellow horse owners help you make informed decisions.',
  },
  {
    icon: Heart,
    title: 'Care Guaranteed',
    description: 'Dispute resolution and secure payments protect both guests and hosts.',
  },
];

const listingTypes = [
  { type: 'stall', label: 'Stalls', image: 'https://images.unsplash.com/photo-1563116128-261f46267cac?w=400' },
  { type: 'pasture', label: 'Pastures', image: 'https://images.unsplash.com/photo-1551053343-65678990726b?w=400' },
  { type: 'layover', label: 'Layover', image: 'https://images.unsplash.com/photo-1634212926530-2cf2ac9e0df9?w=400' },
  { type: 'full_boarding', label: 'Full Boarding', image: 'https://images.unsplash.com/photo-1591330189093-f0a4b8c96a79?w=400' },
];

export default function Landing() {
  const navigate = useNavigate();
  const [searchLocation, setSearchLocation] = useState('');
  const [featuredListings, setFeaturedListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await listingsApi.getAll({ limit: 6, status: 'approved' });
        setFeaturedListings(response.data);
      } catch (error) {
        console.error('Error fetching listings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/search${searchLocation ? `?city=${searchLocation}` : ''}`);
  };

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-bg">
          <img src={heroImage} alt="Horses grazing at sunset" />
          <div className="hero-overlay" />
        </div>

        <div className="hero-content w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl stagger-children">
            <p className="text-farmhouse-saddle tracking-[0.2em] uppercase text-sm font-medium mb-4">
              Find the perfect home for your horses
            </p>
            <h1 className="font-serif text-5xl md:text-7xl font-bold text-farmhouse-cream mb-6 leading-tight">
              Barns & Pastures
            </h1>
            <p className="text-lg md:text-xl text-farmhouse-muted mb-8 max-w-xl">
              Connect with trusted hosts offering quality stables, pastures, and boarding services for your beloved animals.
            </p>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 max-w-lg">
              <div className="relative flex-1">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-farmhouse-muted" />
                <Input
                  type="text"
                  placeholder="City or State..."
                  value={searchLocation}
                  onChange={(e) => setSearchLocation(e.target.value)}
                  className="form-input pl-12 w-full"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" className="btn-primary flex items-center gap-2" data-testid="search-button">
                <Search className="w-4 h-4" />
                Search
              </Button>
            </form>

            {/* Quick stats */}
            <div className="flex gap-8 mt-12 text-farmhouse-muted">
              <div>
                <p className="text-2xl font-serif font-semibold text-farmhouse-cream">500+</p>
                <p className="text-sm">Verified Hosts</p>
              </div>
              <div>
                <p className="text-2xl font-serif font-semibold text-farmhouse-cream">10k+</p>
                <p className="text-sm">Happy Guests</p>
              </div>
              <div>
                <p className="text-2xl font-serif font-semibold text-farmhouse-cream">50</p>
                <p className="text-sm">States Covered</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Browse by Type */}
      <section className="py-20 md:py-32 bg-farmhouse-charcoal" data-testid="browse-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-farmhouse-saddle tracking-[0.2em] uppercase text-sm font-medium mb-3">
              Browse by type
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-farmhouse-cream">
              Find What You Need
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {listingTypes.map((item) => (
              <Link
                key={item.type}
                to={`/search?type=${item.type}`}
                className="group relative aspect-[4/5] rounded-md overflow-hidden card-hover"
                data-testid={`browse-${item.type}`}
              >
                <img
                  src={item.image}
                  alt={item.label}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-farmhouse-charcoal via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="font-serif text-xl font-semibold text-farmhouse-cream group-hover:text-farmhouse-saddle transition-colors">
                    {item.label}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-32 bg-farmhouse-paper" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-farmhouse-saddle tracking-[0.2em] uppercase text-sm font-medium mb-3">
              Why choose us
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-farmhouse-cream">
              Peace of Mind
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="text-center p-8 rounded-md bg-farmhouse-charcoal border border-farmhouse-subtle card-hover"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-farmhouse-saddle/10 flex items-center justify-center">
                  <feature.icon className="w-7 h-7 text-farmhouse-saddle" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-farmhouse-cream mb-3">
                  {feature.title}
                </h3>
                <p className="text-farmhouse-muted leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Listings */}
      <section className="py-20 md:py-32 bg-farmhouse-charcoal" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-farmhouse-saddle tracking-[0.2em] uppercase text-sm font-medium mb-3">
                Featured listings
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-farmhouse-cream">
                Popular Stables
              </h2>
            </div>
            <Link
              to="/search"
              className="hidden md:flex items-center gap-2 text-farmhouse-saddle hover:text-farmhouse-hover transition-colors"
              data-testid="view-all-link"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="listing-card animate-pulse">
                  <div className="aspect-[4/3] bg-farmhouse-subtle" />
                  <div className="p-4 space-y-3">
                    <div className="h-6 bg-farmhouse-subtle rounded w-3/4" />
                    <div className="h-4 bg-farmhouse-subtle rounded w-1/2" />
                    <div className="h-4 bg-farmhouse-subtle rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : featuredListings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredListings.map((listing) => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-farmhouse-muted text-lg mb-4">No listings available yet.</p>
              <Link to="/auth">
                <Button className="btn-primary">Become a Host</Button>
              </Link>
            </div>
          )}

          <div className="md:hidden mt-8 text-center">
            <Link to="/search">
              <Button className="btn-secondary">
                View All Listings
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-farmhouse-paper relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 gradient-radial" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-serif text-4xl md:text-5xl font-semibold text-farmhouse-cream mb-6">
            Have a Barn or Pasture?
          </h2>
          <p className="text-lg text-farmhouse-muted mb-8 max-w-xl mx-auto">
            Join our community of trusted hosts and earn income by sharing your space with horse owners in need.
          </p>
          <Link to="/auth?role=host" data-testid="become-host-cta">
            <Button className="btn-primary text-lg px-8 py-4">
              Become a Host
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-farmhouse-charcoal border-t border-farmhouse-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-6 h-6 text-farmhouse-saddle" />
                <span className="font-serif text-lg font-semibold text-farmhouse-cream">
                  Barns & Pastures
                </span>
              </div>
              <p className="text-farmhouse-muted text-sm leading-relaxed">
                The trusted marketplace for horse and livestock boarding.
              </p>
            </div>

            {/* Explore */}
            <div>
              <h4 className="font-serif text-farmhouse-cream font-semibold mb-4">Explore</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/search" className="text-farmhouse-muted hover:text-farmhouse-saddle transition-colors">
                    Browse Listings
                  </Link>
                </li>
                <li>
                  <Link to="/how-it-works" className="text-farmhouse-muted hover:text-farmhouse-saddle transition-colors">
                    How It Works
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="text-farmhouse-muted hover:text-farmhouse-saddle transition-colors">
                    Blog
                  </Link>
                </li>
              </ul>
            </div>

            {/* Host */}
            <div>
              <h4 className="font-serif text-farmhouse-cream font-semibold mb-4">Host</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/auth?role=host" className="text-farmhouse-muted hover:text-farmhouse-saddle transition-colors">
                    Become a Host
                  </Link>
                </li>
                <li>
                  <Link to="/help" className="text-farmhouse-muted hover:text-farmhouse-saddle transition-colors">
                    Host Resources
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-serif text-farmhouse-cream font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/about" className="text-farmhouse-muted hover:text-farmhouse-saddle transition-colors">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/about#team" className="text-farmhouse-muted hover:text-farmhouse-saddle transition-colors">
                    Our Team
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-farmhouse-muted hover:text-farmhouse-saddle transition-colors">
                    Contact
                  </Link>
                </li>
                <li>
                  <Link to="/help" className="text-farmhouse-muted hover:text-farmhouse-saddle transition-colors">
                    Help Center
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="pt-8 border-t border-farmhouse-subtle flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-farmhouse-muted">
              © 2025 Barns & Pastures. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm text-farmhouse-muted">
              <a href="#" className="hover:text-farmhouse-cream transition-colors">Privacy</a>
              <a href="#" className="hover:text-farmhouse-cream transition-colors">Terms</a>
              <a href="#" className="hover:text-farmhouse-cream transition-colors">Sitemap</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
